import AsyncStorage, { captureAccount } from "../services/accountStorage";
import openhouseAPI from "../services/openhouse";
import { syncOpenHouseAttendees, prepareCompletionAttendees } from "../services/attendeeSubmission";
import queryClient from "../services/reactQuery";
import { classifyFailure, FailureReason } from "../utils/networkHealth";

export type SyncResult = { failed: number; completed: number; failureReason?: FailureReason };
let pendingOperation = Promise.resolve();
const read = async (key: string) => JSON.parse((await AsyncStorage.getItem(key)) || "null");

// Remove only the exact snapshot acknowledged by the server. New edits and failed
// items must remain queued even when another open house succeeds.
const acknowledge = async (key: string, item: any) => {
  const latest = (await read(key)) || [];
  await AsyncStorage.setItem(key, JSON.stringify(latest.filter(
    (entry: any) => JSON.stringify(entry) !== JSON.stringify(item)
  )));
};

const runSync = async (uuid: string | undefined, assertAccount: () => void): Promise<SyncResult> => {
  assertAccount();
  const result: SyncResult = { failed: 0, completed: 0 };
  const failedStarts = new Set<string>();
  const request = async (operation: () => Promise<void>) => {
    try { assertAccount(); await operation(); assertAccount(); return true; }
    catch (error) {
      result.failed++;
      result.failureReason = classifyFailure(error);
      return false;
    }
  };
  if (!uuid) {
    await request(async () => {
      const deleted = await read("deleteList");
      if (!deleted?.openHouses?.length) return;
      await openhouseAPI.delete("/openhouse", { data: { uuids: deleted.openHouses }, timeout: 15000, accountGuard: assertAccount } as any);
      assertAccount();
      const latest = await read("deleteList");
      if (latest) await AsyncStorage.setItem("deleteList", JSON.stringify({
        ...latest, openHouses: latest.openHouses.filter((id: string) => !deleted.openHouses.includes(id)),
      }));
    });
  }
  const starts = (await read("@startedUuids")) || [];
  for (const item of starts.filter((entry: any) => !uuid || entry.uuid === uuid)) {
    const succeeded = await request(async () => {
      await openhouseAPI.post("/openhouse/start", {
        uuid: item.uuid, active_at: item.active_at || new Date().toISOString(), deviceInfo: item.deviceInfo,
      }, { timeout: 15000, accountGuard: assertAccount } as any);
      assertAccount();
      if (!item.completed_at) await acknowledge("@startedUuids", item);
    });
    if (!succeeded) failedStarts.add(item.uuid);
  }
  const completions = (await read("syncData")) || [];
  for (const item of completions.filter((entry: any) => !uuid || entry.data.uuid === uuid)) {
    if (failedStarts.has(item.data.uuid)) continue;
    await request(async () => {
      const started = starts.find((entry: any) => entry.uuid === item.data.uuid);
      const data = { ...item.data };
      if (started) {
        data.active_at = started.active_at;
        data.activeAt = started.active_at;
        data.completedAt = started.completed_at;
      }
      if (data.primaryAgentUserId) {
        await prepareCompletionAttendees(data.uuid, data.attendees);
        assertAccount();
        await syncOpenHouseAttendees(data.uuid, data.primaryAgentUserId);
        data.attendees = [];
      }

      assertAccount();
      await openhouseAPI.post("/openhouse/complete", data, { timeout: 15000, accountGuard: assertAccount } as any);
      assertAccount();
      await acknowledge("syncData", item);
      const remaining = (await read("syncData")) || [];
      if (remaining.some((entry: any) => entry.data.uuid === data.uuid)) {
        result.failed++;
      }
      if (started) await acknowledge("@startedUuids", started);
      result.completed++;
    });
  }
  await Promise.all([
    queryClient.invalidateQueries(["openHouses"]),
    queryClient.invalidateQueries(["dashboardStats"]),
    queryClient.invalidateQueries(["syncData"]),
  ]);
  return result;
};

const useSyncOpenHouses = () => (uuid?: string): Promise<SyncResult> => {
  const assertAccount = captureAccount();
  const operation = pendingOperation.then(() => runSync(uuid, assertAccount));
  pendingOperation = operation.then(() => undefined, () => undefined);
  return operation;
};
export default useSyncOpenHouses;
